import { Checkbox, FormSelect, FormSelectDataType, Modal, PrimaryButton } from '@systemeio/ui'
import React, { useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'

interface CsvExportModalProps<T> {
  title: string
  opened: boolean
  onClose: () => void
  afterLeave?: () => void
  columns: CsvExportColumnInterface<T>[]
  handleExportCsv: (data: ExportOptionsInterface<T>) => Promise<void>
  className?: string
}

export interface CsvExportColumnInterface<T> {
  id: T
  caption: string
}

export interface ExportOptionsInterface<T> extends NonNullable<CsvExportData<T>> {}

export enum DelimeterTypeEnum {
  comma = ',',
  pipe = '|',
  caret = '^',
  semi_colon = ';',
  tilde = '~',
}

export const DelimiterLocoKeys = {
  [DelimeterTypeEnum.comma]: 'dashboard.export_csv.delimiter_comma',
  [DelimeterTypeEnum.pipe]: 'dashboard.export_csv.delimiter_pipe',
  [DelimeterTypeEnum.caret]: 'dashboard.export_csv.delimiter_caret',
  [DelimeterTypeEnum.semi_colon]: 'dashboard.export_csv.delimiter_semi-colon',
  [DelimeterTypeEnum.tilde]: 'dashboard.export_csv.delimiter_tilde',
}

export interface DelimiterDataInterface {
  id: DelimeterTypeEnum
  caption: typeof DelimiterLocoKeys[DelimeterTypeEnum]
}

export interface CsvExportData<T> {
  exportColumns: T[]
  delimiter: DelimeterTypeEnum | undefined
}

const CsvExportModal = <T,>({
  title,
  columns,
  opened,
  onClose,
  handleExportCsv,
  afterLeave,
  className,
}: CsvExportModalProps<T>) => {
  const { t } = useLocoTranslation()
  const [isFetching, setIsFetching] = useState(false)
  const [data, setData] = useState<CsvExportData<T>>({
    exportColumns: [],
    delimiter: DelimeterTypeEnum.comma,
  })

  const getDelimiterDate = (): FormSelectDataType<DelimeterTypeEnum>[] =>
    Object.keys(DelimiterLocoKeys).map(el => {
      const key = el as DelimeterTypeEnum
      return { caption: t(DelimiterLocoKeys[key]), id: key }
    })

  const onExport = async () => {
    try {
      setIsFetching(true)
      await handleExportCsv(data)
      setIsFetching(false)
    } catch (e) {
      setIsFetching(false)
    }
  }

  const onModalClose = () => setData({ delimiter: DelimeterTypeEnum.comma, exportColumns: [] })

  return (
    <Modal
      className={className}
      isFetching={isFetching}
      title={title}
      opened={opened}
      onClose={onClose}
      afterLeave={() => {
        onModalClose()
        afterLeave && afterLeave()
      }}
    >
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-3">
            <div className="flex gap-0.5">
              <span>{t('dashboard.export_csv.columns_export')}</span>
              <span className="text-danger">*</span>
            </div>
            <ul className="flex flex-col gap-2">
              {columns.map((el, idx) => {
                const isSelected = data.exportColumns.includes(el.id)
                return (
                  <li key={idx}>
                    <Checkbox
                      value={isSelected}
                      label={el.caption}
                      onChange={() =>
                        isSelected
                          ? setData(prev => ({
                              ...prev,
                              exportColumns: prev.exportColumns.filter(column => column !== el.id),
                            }))
                          : setData(prev => ({
                              ...prev,
                              exportColumns: [...prev.exportColumns, el.id],
                            }))
                      }
                    />
                  </li>
                )
              })}
            </ul>
          </div>
          <div className="flex flex-col gap-3">
            <FormSelect
              label={t('dashboard.export_csv.delimiter')}
              placeholder={t('dashboard.export_csv.select_delimiter')}
              onChange={value => setData(prev => ({ ...prev, delimiter: value }))}
              data={getDelimiterDate()}
              value={data.delimiter}
              required
            />
          </div>
        </div>
        <div className="flex justify-center">
          <PrimaryButton
            isFetching={isFetching}
            disabled={!data.delimiter || data.exportColumns.length === 0}
            onClick={onExport}
          >
            {t('global.export')}
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  )
}

export default CsvExportModal
